import { Card, CardContent } from '@design-system/Card';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TRISYNBIOTIC_DETAIL_PATH } from '@/constants/paths';
import { useATSData } from './useATSData';
import { ATS_PILL } from '@/constants/images';
import { useNutritionViewModal } from '@/features/NutritionsViewModels/NutritionProducts.view.model';
import { getAddOnFormulaImageUrl } from '@/utils/nutrition/getFormulaImageUrl';
import { getMyIngredientsPageUrl } from '@/utils/nutrition/getMyIngredientsPageUrl';

export interface TriSynbioticTaskCardProps {
  variant?: 'formulas';
}

export const TriSynbioticTaskCard = ({ variant }: TriSynbioticTaskCardProps = {}) => {
  const navigate = useNavigate();
  const { data, isLoading } = useATSData();
  const { foodFormulaData } = useNutritionViewModal();
  const atsProduct = foodFormulaData?.find((item) => item.is_addon);
  const isFromFormulas = variant === 'formulas';

 
  if (!isFromFormulas && (isLoading || !data)) {
    return null;
  }

  const handleNavigate = () => {
    if (isFromFormulas && atsProduct) {
      navigate(getMyIngredientsPageUrl({ product: atsProduct.name }));
      return;
    }
    navigate(TRISYNBIOTIC_DETAIL_PATH);
  };

  const title = isFromFormulas ? atsProduct?.name : `Start ${data?.title}`;
  const description = isFromFormulas
    ? 'Fortifying Gut Lining'
    : `Start your 30-day gut prep with Viome ${data?.title}.`;

  return (
    <Card
      data-testid="home-triSynbioticTask"
      variant={isFromFormulas ? 'glass' : undefined}
      className={
        isFromFormulas
          ? 'relative mt-8 cursor-pointer'
          : 'relative bg-background-blur-medium before:absolute before:shadow-glass-inner cursor-pointer'
      }
      onClick={handleNavigate}
    >
      <CardContent className="flex items-center gap-4 p-4">
        <div className="shrink-0 w-[70px] h-[70px]">
          <img
            src={
              atsProduct?.image_url
                ? getAddOnFormulaImageUrl(atsProduct.image_url)
                : ATS_PILL
            }
            alt="Advanced TriSynbiotic™"
            className="w-full h-full object-contain mix-blend-multiply"
          />
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <span className="typography-h3 text-secondary leading-snug">
            {title}
          </span>
          <span
            className={
              isFromFormulas
                ? 'typography-title font-serif italic font-light text-primary'
                : 'typography-body text-secondary text-sm'
            }
          >
            {description}
          </span>
          {isFromFormulas && !!atsProduct?.ingredients_count && (
            <span className="typography-caption1 text-tertiary">
              {atsProduct.ingredients_count} ingredients
            </span>
          )}
          {!isFromFormulas && (
            <span className="flex items-center text-success text-sm font-light mt-1">
              View details
              <ChevronRight className="h-5 w-5 ml-0.5" />
            </span>
          )}
        </div>

        {isFromFormulas && (
          <span className="flex shrink-0 items-center text-success text-sm font-light">
            View details
            <ChevronRight className="h-5 w-5 ml-0.5" />
          </span>
        )}
      </CardContent>
    </Card>
  );
};
